import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical, BotOff, Bot } from "lucide-react";

import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import AddToFunnelModal from "../AddToFunnelModal";

const TicketOptionsMenu = ({ ticket }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [funnelModalOpen, setFunnelModalOpen] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const navigate = useNavigate();

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	// Cala o robô só nesta conversa. Quem está atendendo às vezes precisa que
	// o fluxo, o bot da fila e a IA parem de responder por cima dele — antes
	// isso só era possível desligando na conexão, o que afeta todo mundo.
	const [pausado, setPausado] = useState(Boolean(ticket.botPaused));

	const alternarRobo = async () => {
		const novo = !pausado;

		setMenuOpen(false);

		try {
			await api.put(`/tickets/${ticket.id}/bot`, { paused: novo });
			setPausado(novo);
			toast.success(
				novo
					? "Robô pausado nesta conversa."
					: "Robô liberado para responder aqui."
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
		setMenuOpen(false);
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};


	return (
		<>
			<AddToFunnelModal
				open={funnelModalOpen}
				onClose={() => setFunnelModalOpen(false)}
				contact={ticket.contact}
				ticketId={ticket.id}
			/>
			<DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="icon">
						<MoreVertical className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem onClick={alternarRobo}>
						{pausado ? (
							<Bot className="h-4 w-4" />
						) : (
							<BotOff className="h-4 w-4" />
						)}
						{pausado ? "Reativar robô" : "Pausar robô (fluxo e IA)"}
					</DropdownMenuItem>
					<DropdownMenuItem onClick={handleOpenTransferModal}>
						{i18n.t("ticketOptionsMenu.transfer")}
					</DropdownMenuItem>
					{/* Grupo não vira oportunidade: o negócio é com uma pessoa, e
					    o contato de um grupo é o grupo inteiro. */}
					{!ticket.isGroup && (
						<DropdownMenuItem
							onClick={() => {
								setMenuOpen(false);
								setFunnelModalOpen(true);
							}}
						>
							Adicionar ao funil
						</DropdownMenuItem>
					)}
					<Can
						role={user.profile}
						perform="ticket-options:replay"
						yes={() => (
							<DropdownMenuItem
								onClick={() => navigate(`/tickets/${ticket.id}/replay`)}
							>
								Ver replay
							</DropdownMenuItem>
						)}
					/>
					<Can
						role={user.profile}
						perform="ticket-options:deleteTicket"
						yes={() => (
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onClick={() => {
									setConfirmationOpen(true);
									setMenuOpen(false);
								}}
							>
								{i18n.t("ticketOptionsMenu.delete")}
							</DropdownMenuItem>
						)}
					/>
				</DropdownMenuContent>
			</DropdownMenu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
			/>
		</>
	);
};

export default TicketOptionsMenu;
